// Power Safety Solutions — client behaviour.
// Replaces jQuery, Bootstrap JS and the legacy plugin stack with a small, framework-free module.

interface AjaxResponse {
  result?: boolean;
  message?: string;
}

/** Mobile navigation toggle (replaces Bootstrap's collapse plugin). */
function initNavToggle(): void {
  const toggle = document.querySelector<HTMLButtonElement>("[data-nav-toggle]");
  const menu = document.querySelector<HTMLElement>("[data-nav-menu]");
  if (!toggle || !menu) {
    return;
  }

  toggle.addEventListener("click", () => {
    const open = menu.classList.toggle("open");
    toggle.setAttribute("aria-expanded", String(open));
  });
}

/** Combine the two native date inputs into the single CourseDates field the server expects. */
function initCourseDates(form: HTMLFormElement): void {
  const from = form.querySelector<HTMLInputElement>("[data-course-from]");
  const to = form.querySelector<HTMLInputElement>("[data-course-to]");
  const hidden = form.querySelector<HTMLInputElement>("#CourseDates");
  if (!hidden) {
    return;
  }

  const sync = (): void => {
    const f = from?.value ?? "";
    const t = to?.value ?? "";
    hidden.value = f && t ? `${f} to ${t}` : f || t;
  };

  from?.addEventListener("change", sync);
  to?.addEventListener("change", sync);
}

/** Booking form: native validation + fetch() submit (replaces parsley + jQuery $.post). */
function initBookingForm(): void {
  const form = document.querySelector<HTMLFormElement>("[data-ajax-form]");
  if (!form) {
    return;
  }

  initCourseDates(form);

  const alertBox = document.querySelector<HTMLElement>("[data-form-alert]");
  const button = form.querySelector<HTMLButtonElement>("#submit-button");
  const label = form.querySelector<HTMLElement>("[data-submit-label]");
  const defaultLabel = label?.textContent ?? "Send booking request";

  const showAlert = (message: string, ok: boolean): void => {
    if (!alertBox) {
      return;
    }
    alertBox.textContent = message;
    alertBox.classList.remove("alert-success", "alert-danger");
    alertBox.classList.add("is-visible", ok ? "alert-success" : "alert-danger");
  };

  form.addEventListener("submit", async (e: SubmitEvent) => {
    e.preventDefault();

    form.classList.add("was-validated");
    if (!form.checkValidity()) {
      form.reportValidity();
      return;
    }

    if (button) {
      button.disabled = true;
    }
    if (label) {
      label.textContent = "Sending…";
    }

    try {
      // FormData includes the anti-forgery token hidden field rendered by the form tag helper.
      const response = await fetch(form.action, {
        method: "POST",
        body: new FormData(form),
        headers: { "X-Requested-With": "fetch" },
      });
      const data = (await response.json()) as AjaxResponse;

      if (response.ok && data.result) {
        showAlert(data.message ?? "Thank you, we've received your booking.", true);
        form.style.display = "none";
      } else {
        showAlert(data.message ?? "Sorry, we could not send your message.", false);
      }
    } catch {
      showAlert("Sorry, we could not send your message. Please try again or call us.", false);
    } finally {
      if (button) {
        button.disabled = false;
      }
      if (label) {
        label.textContent = defaultLabel;
      }
    }
  });
}

function ready(fn: () => void): void {
  if (document.readyState !== "loading") {
    fn();
  } else {
    document.addEventListener("DOMContentLoaded", fn);
  }
}

ready(() => {
  initNavToggle();
  initBookingForm();
});
